import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { PLANBASE_CONFIG } from '../config';
import { MobilePageHeader } from '../components/MobilePageHeader';
import { useHideBottomNav } from '../hooks/useHideBottomNav';

import { ArrowPathIcon, EyeIcon, EyeSlashIcon, LockClosedIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

export default function SecuritySettings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  useHideBottomNav();
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const set = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match.');
      return;
    }
    if (formData.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('planbase_token');
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Project-ID': PLANBASE_CONFIG.projectId,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Password changed successfully.');
        setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        toast.error(data.error || 'Failed to change password. Check your current password.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async () => {
    if (!user?.email) return;
    setResetLoading(true);
    try {
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/auth/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          project_id: PLANBASE_CONFIG.projectId,
          redirect_url: `${window.location.origin}/auth/reset-password`,
          project_name: PLANBASE_CONFIG.project.name,
          project_logo: PLANBASE_CONFIG.project.logo_url,
          primary_color: PLANBASE_CONFIG.project.primary_color,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Password reset email sent. Check your inbox.');
      } else {
        toast.error(data.error || 'Failed to send reset email.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <MobilePageHeader title="Security" onBack={() => navigate('/profile')} />
      <div className="hidden md:flex items-center justify-between">
        <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">Security</h1>
        <button type="button" onClick={() => navigate('/profile')}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors">Back</button>
      </div>

      <div className="bg-white dark:bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border">
        <div className="px-4 py-3 bg-muted/40">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Password</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-4 py-3 flex items-center gap-4 border-b border-border">
            <span className="text-xs text-muted-foreground w-36 flex-shrink-0">Current Password</span>
            <div className="flex-1 relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={set('currentPassword')}
                required
                placeholder="Enter current password"
                className="w-full h-8 pl-3 pr-9 border border-input rounded-lg text-sm bg-background focus:outline-none focus:border-primary"
              />
              <button type="button" onClick={() => setShowCurrent(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showCurrent ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>

          <div className="px-4 py-3 flex items-center gap-4 border-b border-border">
            <span className="text-xs text-muted-foreground w-36 flex-shrink-0">New Password</span>
            <div className="flex-1 relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={set('newPassword')}
                required
                placeholder="At least 8 characters"
                className="w-full h-8 pl-3 pr-9 border border-input rounded-lg text-sm bg-background focus:outline-none focus:border-primary"
              />
              <button type="button" onClick={() => setShowNew(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showNew ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>

          <div className="px-4 py-3 flex items-center gap-4">
            <span className="text-xs text-muted-foreground w-36 flex-shrink-0">Confirm Password</span>
            <div className="flex-1 relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={set('confirmPassword')}
                required
                placeholder="Repeat new password"
                className="w-full h-8 pl-3 pr-9 border border-input rounded-lg text-sm bg-background focus:outline-none focus:border-primary"
              />
              <button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showConfirm ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>

          <div className="px-4 py-3 bg-muted/40 border-t border-border flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="h-8 px-4 rounded-lg bg-primary text-white text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {loading
                ? <><ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Saving...</>
                : <><LockClosedIcon className="h-3.5 w-3.5" /> Change Password</>
              }
            </button>
          </div>
        </form>
      </div>

      {/* Reset via email */}
      <div className="bg-white dark:bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border">
        <div className="px-4 py-3 bg-muted/40">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Reset Password</p>
        </div>
        <div className="px-4 py-3 flex items-center gap-4">
          <div className="flex-1">
            <p className="text-sm text-foreground">Request a password reset email</p>
            <p className="text-xs text-muted-foreground mt-0.5">We'll send a reset link to <span className="text-foreground">{user?.email}</span></p>
          </div>
          <button
            type="button"
            onClick={handleRequestReset}
            disabled={resetLoading}
            className="h-8 px-4 rounded-lg border border-border text-xs font-medium hover:bg-muted/60 transition-colors disabled:opacity-50 flex items-center gap-1.5 flex-shrink-0"
          >
            {resetLoading
              ? <><ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Sending...</>
              : <><PaperAirplaneIcon className="h-3.5 w-3.5" /> Send Reset Email</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
